import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  actionMarkPicksSubmitted,
  actionMarkPicksWriting,
  actionRemovePicks,
} from 'lib/actions';
import { getStagedPicksToWrite } from 'lib/selectors/getStagedPicksToWrite';
import { getWritingPicksByWoo } from 'lib/selectors/getPicking';
import useSubmitPicks from 'lib/hooks/useSubmitPicks';
import { throwSuccessToast, throwErrorToast } from 'lib/utils/toast';

export const FLUSH_RETRY_DELAY_MS = 15000;
export const FLUSH_MAX_ATTEMPTS = 3;

/**
 * Writes picks staged at job start (MaterialPickingWizard, mode "start-job") to the
 * WOO's plConsumptions once the server confirms a new start of that WOO (see
 * getStagedPicksToWrite). Until then the picks exist only in Redux, so a failed
 * start records nothing and a retry reuses the same staged picks and pickIds
 * instead of adding a second set.
 *
 * Once their start is confirmed the picks are marked 'writing'. From then on they
 * belong to that start: a later stage or back-out of the WOO leaves them alone,
 * they count as picked material, and the write continues from Redux even if the
 * machine has left the WOO or this hook remounts.
 *
 * A failed write is retried after FLUSH_RETRY_DELAY_MS, up to FLUSH_MAX_ATTEMPTS.
 * After the last failure those picks are removed, so material that was never
 * recorded does not count as picked, and the operator is told to check the
 * Materials Log and add what is missing: a write whose response was lost may have
 * landed, and the log shows the picks recorded on the WOO.
 *
 * Mounted only where material picking is enabled (StagedPicksFlusher).
 */
const useFlushStagedPicks = () => {
  const dispatch = useDispatch();
  const toWrite = useSelector(getStagedPicksToWrite);
  const writingByWoo = useSelector(getWritingPicksByWoo);
  const submitPicks = useSubmitPicks();

  // useSubmitPicks returns a new function every render; read it through a ref so
  // it does not re-trigger the effect (which would skip the retry delay).
  const submitRef = useRef(submitPicks);
  submitRef.current = submitPicks;

  const inFlight = useRef(false);
  const waitingToRetry = useRef(false);
  const attempts = useRef({});
  const retryTimer = useRef(null);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    return () => {
      clearTimeout(retryTimer.current);
    };
  }, []);

  // A confirmed start's staged picks move to 'writing', which the effect below
  // writes.
  useEffect(() => {
    if (!toWrite?.picks?.length) return;
    dispatch(
      actionMarkPicksWriting(
        toWrite.workOrderOperationRef,
        toWrite.picks.map((pick) => {
          return pick.id;
        })
      )
    );
  }, [toWrite, dispatch]);

  useEffect(() => {
    if (inFlight.current || waitingToRetry.current) return;
    const key = Object.keys(writingByWoo)[0];
    if (key == null) return;

    // picksByWoo is keyed by the numeric workOrderOperationRef.
    const wooRef = Number(key);
    const picks = writingByWoo[key];
    const pickIds = picks.map((pick) => {
      return pick.id;
    });
    const attempt = (attempts.current[key] || 0) + 1;
    attempts.current[key] = attempt;
    inFlight.current = true;

    submitRef
      .current(wooRef, picks)
      .then(() => {
        inFlight.current = false;
        delete attempts.current[key];
        dispatch(actionMarkPicksSubmitted(wooRef, pickIds));
        throwSuccessToast('Material picks recorded');
      })
      .catch(() => {
        inFlight.current = false;
        if (attempt < FLUSH_MAX_ATTEMPTS) {
          waitingToRetry.current = true;
          retryTimer.current = setTimeout(() => {
            waitingToRetry.current = false;
            setRetryTick((tick) => {
              return tick + 1;
            });
          }, FLUSH_RETRY_DELAY_MS);
        } else {
          delete attempts.current[key];
          dispatch(actionRemovePicks(wooRef, pickIds));
          throwErrorToast(
            'Failed to record material picks. Check the Materials Log and add any that are missing.'
          );
        }
      });
  }, [writingByWoo, retryTick, dispatch]);
};

export default useFlushStagedPicks;
